import type { CSSProperties, ReactNode } from "react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import FrameComponent from "react-frame-component";
import type { FrameComponentProps } from "react-frame-component";

const HEIGHT_PADDING = 10;

const responsiveFrame: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};

/**
 * ## Iframe Height Adaptation Logic
 *
 * When `shouldAdaptHeightToContentSize` is enabled, this component automatically adjusts the iframe
 * height to match its content. The adaptation process works as follows:
 *
 * ### 1. Height Calculation (`calculateContentHeight`)
 *    - First checks for a `.frame-content` container element (if present, uses that for measurement)
 *    - Falls back to measuring `body` and `documentElement` if no container exists
 *    - Uses `Math.max()` of multiple height properties to ensure accurate measurement:
 *      - `scrollHeight`: Total scrollable content height (includes overflow)
 *      - `offsetHeight`: Visible height including borders and padding
 *      - `clientHeight`: Visible height excluding borders and scrollbars
 *    - Adds `HEIGHT_PADDING` (10px) to prevent content from being cut off at edges
 *
 * ### 2. Height Update Timing (`updateHeight`)
 *    - Uses double `requestAnimationFrame` to ensure layout is fully settled before measuring
 *    - First RAF: Forces reflow by reading `offsetHeight` properties
 *    - Second RAF: Calculates and applies the final height after layout is complete
 *    - This two-phase approach prevents measuring during layout shifts or incomplete renders
 *
 * ### 3. Content Change Detection (`setupObservers`)
 *    - Sets up ResizeObserver to detect when content dimensions change
 *    - Sets up MutationObserver to detect DOM changes (additions, removals, attribute changes)
 *    - Observes both `.frame-content` (if present) and `body` to catch all changes
 *    - Both observers trigger `updateHeight` when changes are detected
 *
 * ### 4. Initialization (`useAdaptiveHeight`)
 *    - Waits for iframe to load before setting up observers
 *    - Handles both "load" event and already-loaded iframe (checks `readyState`)
 *    - Cleans up observers and event listeners on unmount or dependency changes
 *    - Uses a ref key system to re-initialize when the frame node changes
 *
 * ### Why This Approach?
 *    - `scrollHeight` is critical: It includes overflow content that `offsetHeight` might miss
 *    - Double RAF prevents race conditions where content is measured before layout completes
 *    - Multiple observers ensure height updates for both size changes and DOM mutations
 *    - Observing both container and body catches changes in different parts of the document
 */

export interface ResponsiveFrameProps extends Omit<FrameComponentProps, "ref"> {
  children: ReactNode;
  shouldAdaptHeightToContentSize?: boolean;
}

interface FrameNode {
  contentDocument?: Document;
  node?: HTMLIFrameElement;
}

type FrameRef = FrameNode | HTMLIFrameElement | null;

const getIframeElement = (frameNode: FrameRef): HTMLIFrameElement | null => {
  if (!frameNode) return null;
  if (frameNode instanceof HTMLIFrameElement) return frameNode;
  if ("node" in frameNode && frameNode.node instanceof HTMLIFrameElement) {
    return frameNode.node;
  }
  return null;
};

const getFrameDocument = (iframe: HTMLIFrameElement | null): Document | null =>
  iframe?.contentDocument ?? null;

const calculateContentHeight = (doc: Document): number => {
  const frameContent = doc.querySelector<HTMLElement>(".frame-content");
  if (frameContent) {
    return (
      Math.max(
        frameContent.scrollHeight,
        frameContent.offsetHeight,
        frameContent.clientHeight,
      ) + HEIGHT_PADDING
    );
  }

  const { body, documentElement } = doc;
  return (
    Math.max(
      body.scrollHeight,
      documentElement.scrollHeight,
      body.offsetHeight,
      documentElement.offsetHeight,
      body.clientHeight,
      documentElement.clientHeight,
    ) + HEIGHT_PADDING
  );
};

const updateHeight = (iframe: HTMLIFrameElement | null): void => {
  if (!iframe) return;

  try {
    const doc = getFrameDocument(iframe);
    if (!doc?.body || !doc.documentElement) return;

    globalThis.requestAnimationFrame(() => {
      const bodyOffset = doc.body.offsetHeight;
      const htmlOffset = doc.documentElement.offsetHeight;
      if (bodyOffset >= 0 && htmlOffset >= 0) {
        globalThis.requestAnimationFrame(() => {
          const newHeight = calculateContentHeight(doc);
          iframe.style.height = `${newHeight}px`;
        });
      }
    });
  } catch (err) {
    console.error("ResponsiveFrame: Failed to update height", err);
  }
};

const setupObservers = (
  iframe: HTMLIFrameElement | null,
  updateHeightFn: () => void,
): {
  resizeObserver: ResizeObserver | null;
  mutationObserver: MutationObserver | null;
} => {
  const doc = getFrameDocument(iframe);
  if (!doc?.body) {
    return { resizeObserver: null, mutationObserver: null };
  }

  const resizeObserver = globalThis.ResizeObserver
    ? new ResizeObserver(updateHeightFn)
    : null;

  const mutationObserver = globalThis.MutationObserver
    ? new MutationObserver(updateHeightFn)
    : null;

  const frameContent = doc.querySelector<HTMLElement>(".frame-content");
  const targetElement = frameContent || doc.body;

  if (resizeObserver) {
    resizeObserver.observe(targetElement);
    if (frameContent && targetElement !== doc.body) {
      resizeObserver.observe(doc.body);
    }
  }

  if (mutationObserver) {
    mutationObserver.observe(targetElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }

  return { resizeObserver, mutationObserver };
};

const useAdaptiveHeight = (
  frameNodeRef: React.RefObject<FrameRef>,
  shouldAdapt: boolean,
  frameNodeKey: number,
): void => {
  const observersRef = useRef<{
    resizeObserver: ResizeObserver | null;
    mutationObserver: MutationObserver | null;
  }>({ resizeObserver: null, mutationObserver: null });

  useEffect(() => {
    if (!shouldAdapt) return;

    const frameNode = frameNodeRef.current;
    const iframe = getIframeElement(frameNode);
    if (!iframe) return;

    const handleLoad = (): void => {
      updateHeight(iframe);
      const observers = setupObservers(iframe, () => updateHeight(iframe));
      observersRef.current = observers;
    };

    iframe.addEventListener("load", handleLoad);

    if (iframe.contentDocument?.readyState === "complete") {
      handleLoad();
    }

    return () => {
      iframe.removeEventListener("load", handleLoad);
      observersRef.current.resizeObserver?.disconnect();
      observersRef.current.mutationObserver?.disconnect();
    };
  }, [frameNodeRef, shouldAdapt, frameNodeKey]);
};

export const ResponsiveFrame = memo(
  ({
    children,
    style,
    shouldAdaptHeightToContentSize = false,
    ...rest
  }: ResponsiveFrameProps) => {
    const frameNodeRef = useRef<FrameRef>(null);
    const [frameNodeKey, setFrameNodeKey] = useState(0);

    const frameRef = useCallback(
      (node: FrameComponent | HTMLIFrameElement | null) => {
        frameNodeRef.current = node as FrameRef;
        setFrameNodeKey(prev => prev + 1);
      },
      [],
    );

    useAdaptiveHeight(
      frameNodeRef,
      shouldAdaptHeightToContentSize,
      frameNodeKey,
    );

    return (
      <FrameComponent
        ref={frameRef}
        style={{ ...responsiveFrame, ...style }}
        {...rest}
      >
        {children}
      </FrameComponent>
    );
  },
);

ResponsiveFrame.displayName = "ResponsiveFrame";
